
import { useEffect, useState, useRef } from "react";
import { X, Loader2, Upload, ImageIcon, Video, Type, Users, Clock, ChevronDown } from "lucide-react";
import { listarEdicoes } from "@/lib/edicoes";
import { listarConfirmados, listarEspera } from "@/lib/participantes";
import { criarCampanha, uploadMidia } from "@/lib/campanhas";
import { Edicao } from "@/types/edicao";
import { Participante, Confirmado } from "@/types/participante";
import { TipoMensagem, FonteContatos } from "@/types/campanha";

const inputCls =
  "w-full px-3.5 py-2.5 rounded-xl border border-black/10 bg-black/[0.02] text-sm text-black placeholder-black/25 outline-none focus:border-black focus:bg-white transition";

const selectCls =
  "w-full pl-3.5 pr-9 py-2.5 rounded-xl border border-black/10 bg-black/[0.02] text-sm text-black outline-none focus:border-black focus:bg-white transition cursor-pointer appearance-none";

interface Props {
  onClose: () => void;
  onCriada: (id: string) => void;
}

export default function NovaCampanhaModal({ onClose, onCriada }: Props) {
  const [edicoes, setEdicoes] = useState<Edicao[]>([]);
  const [carregandoEdicoes, setCarregandoEdicoes] = useState(true);

  const [nome, setNome] = useState("");
  const [edicaoId, setEdicaoId] = useState("");
  const [fonte, setFonte] = useState<FonteContatos>("confirmados");
  const [tipoMensagem, setTipoMensagem] = useState<TipoMensagem>("texto");
  const [mensagem, setMensagem] = useState("");
  const [midiaFile, setMidiaFile] = useState<File | null>(null);
  const [midiaPreview, setMidiaPreview] = useState("");

  const [contatosPreview, setContatosPreview] = useState<number | null>(null);
  const [carregandoContatos, setCarregandoContatos] = useState(false);

  const [salvando, setSalvando] = useState(false);
  const [etapa, setEtapa] = useState("");
  const [erro, setErro] = useState("");

  const fileRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    listarEdicoes()
      .then((lista) => { setEdicoes(lista); })
      .finally(() => setCarregandoEdicoes(false));
  }, []);

  // Preview de contatos ao mudar edição/fonte
  useEffect(() => {
    if (!edicaoId) { setContatosPreview(null); return; }
    setCarregandoContatos(true);
    const fn = fonte === "confirmados"
      ? listarConfirmados(edicaoId)
      : listarEspera(edicaoId);
    fn.then((lista) => setContatosPreview(lista.length))
      .finally(() => setCarregandoContatos(false));
  }, [edicaoId, fonte]);

  const handleFile = (file: File) => {
    setMidiaFile(file);
    setMidiaPreview(URL.createObjectURL(file));
  };

  const inserirVariavel = () => {
    setMensagem((m) => m + "{nome}");
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErro("");

    if (!edicaoId) { setErro("Selecione uma edição."); return; }
    if (!mensagem.trim()) { setErro("A mensagem não pode estar vazia."); return; }
    if (tipoMensagem !== "texto" && !midiaFile) {
      setErro("Selecione o arquivo de mídia."); return;
    }

    setSalvando(true);
    try {
      setEtapa("Carregando contatos...");
      const contatos: (Participante | Confirmado)[] = fonte === "confirmados"
        ? await listarConfirmados(edicaoId)
        : await listarEspera(edicaoId);

      if (contatos.length === 0) {
        setErro("Nenhum contato encontrado nessa lista."); return;
      }

      const edicao = edicoes.find((e) => e.id === edicaoId)!;

      // Upload de mídia (cria ID temporário para pasta)
      let midiaUrl: string | undefined;
      if (midiaFile) {
        setEtapa("Enviando mídia...");
        midiaUrl = await uploadMidia(midiaFile, `tmp_${Date.now()}`);
      }

      setEtapa(`Criando ${contatos.length} disparos...`);
      const id = await criarCampanha({
        nome,
        edicaoId,
        edicaoNome: `${edicao.nomeLocal} — ${edicao.cidade}/${edicao.uf}`,
        fonte,
        tipoMensagem,
        mensagem: mensagem.trim(),
        midiaUrl,
        contatos,
      });

      onCriada(id);
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      setErro(`Erro ao criar campanha: ${msg}`);
    } finally {
      setSalvando(false);
      setEtapa("");
    }
  };

  const edicaoSelecionada = edicoes.find((e) => e.id === edicaoId);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={!salvando ? onClose : undefined} />
      <div className="relative w-full max-w-lg bg-white rounded-2xl shadow-2xl flex flex-col max-h-[92vh]">

        {/* Header */}
        <div className="flex items-center justify-between px-6 py-5 border-b border-black/5">
          <h2 className="text-base font-semibold text-black">Nova Campanha</h2>
          {!salvando && (
            <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-lg text-black/30 hover:text-black hover:bg-black/5 transition">
              <X className="w-4 h-4" />
            </button>
          )}
        </div>

        {/* Body */}
        <form id="form-campanha" onSubmit={handleSubmit} className="overflow-y-auto px-6 py-5 space-y-5">

          {/* Nome */}
          <div className="space-y-1.5">
            <label className="text-xs font-medium text-black/60">Nome da campanha</label>
            <input required type="text" placeholder="Ex: Divulgação Edição SP" value={nome}
              onChange={(e) => setNome(e.target.value)} className={inputCls} />
          </div>

          {/* Edição */}
          <div className="space-y-1.5">
            <label className="text-xs font-medium text-black/60">Edição</label>
            {carregandoEdicoes ? (
              <div className="flex items-center gap-2 text-xs text-black/30 py-2">
                <Loader2 className="w-3.5 h-3.5 animate-spin" /> Carregando edições...
              </div>
            ) : (
              <div className="relative">
                <select required value={edicaoId} onChange={(e) => setEdicaoId(e.target.value)} className={selectCls}>
                  <option value="">Selecione uma edição</option>
                  {edicoes.map((e) => (
                    <option key={e.id} value={e.id}>
                      {e.nomeLocal} — {e.cidade}/{e.uf}
                    </option>
                  ))}
                </select>
                <ChevronDown className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-black/30" />
              </div>
            )}
          </div>

          {/* Fonte */}
          <div className="space-y-1.5">
            <label className="text-xs font-medium text-black/60">Lista de contatos</label>
            <div className="grid grid-cols-2 gap-2">
              {(["confirmados", "listaEspera"] as FonteContatos[]).map((f) => (
                <button key={f} type="button" onClick={() => setFonte(f)}
                  className={`flex items-center gap-2 px-4 py-2.5 rounded-xl border text-sm font-medium transition ${
                    fonte === f
                      ? "border-black bg-black text-white"
                      : "border-black/10 text-black/50 hover:border-black/20"
                  }`}>
                  <Users className="w-4 h-4 shrink-0" />
                  {f === "confirmados" ? "Confirmados" : "Lista de espera"}
                </button>
              ))}
            </div>
            {edicaoId && (
              <p className="text-xs text-black/30 flex items-center gap-1.5">
                {carregandoContatos
                  ? <><Loader2 className="w-3 h-3 animate-spin" /> Contando...</>
                  : contatosPreview !== null
                    ? <><span className="text-black/50 font-medium">{contatosPreview}</span> contato{contatosPreview !== 1 ? "s" : ""} encontrado{contatosPreview !== 1 ? "s" : ""} em <strong>{edicaoSelecionada?.nomeLocal}</strong></>
                    : null
                }
              </p>
            )}
          </div>

          {/* Tipo de mensagem */}
          <div className="space-y-1.5">
            <label className="text-xs font-medium text-black/60">Tipo de mensagem</label>
            <div className="grid grid-cols-3 gap-2">
              {([
                { key: "texto", label: "Texto", Icon: Type },
                { key: "foto",  label: "Foto",  Icon: ImageIcon },
                { key: "video", label: "Vídeo", Icon: Video },
              ] as { key: TipoMensagem; label: string; Icon: React.ElementType }[]).map(({ key, label, Icon }) => (
                <button key={key} type="button" onClick={() => { setTipoMensagem(key); setMidiaFile(null); setMidiaPreview(""); }}
                  className={`flex flex-col items-center gap-1.5 py-3 rounded-xl border text-xs font-medium transition ${
                    tipoMensagem === key
                      ? "border-black bg-black text-white"
                      : "border-black/10 text-black/50 hover:border-black/20"
                  }`}>
                  <Icon className="w-4 h-4" />
                  {label}
                </button>
              ))}
            </div>
          </div>

          {/* Upload de mídia */}
          {tipoMensagem !== "texto" && (
            <div className="space-y-1.5">
              <label className="text-xs font-medium text-black/60">
                {tipoMensagem === "foto" ? "Imagem" : "Vídeo"}
              </label>
              <div
                onClick={() => fileRef.current?.click()}
                className={`border-2 border-dashed rounded-xl p-4 flex flex-col items-center gap-2 cursor-pointer transition text-center ${
                  midiaPreview ? "border-black/20 bg-black/[0.02]" : "border-black/10 hover:border-black/20"
                }`}
              >
                {midiaPreview ? (
                  tipoMensagem === "foto"
                    ? <img src={midiaPreview} alt="Preview" className="max-h-32 rounded-lg object-contain" />
                    : <video src={midiaPreview} className="max-h-32 rounded-lg" controls />
                ) : (
                  <>
                    <Upload className="w-5 h-5 text-black/20" />
                    <p className="text-xs text-black/40">
                      Clique para selecionar {tipoMensagem === "foto" ? "uma imagem" : "um vídeo"}
                    </p>
                  </>
                )}
              </div>
              <input ref={fileRef} type="file"
                accept={tipoMensagem === "foto" ? "image/*" : "video/*"}
                className="hidden"
                onChange={(e) => { const f = e.target.files?.[0]; if (f) handleFile(f); }}
              />
              {midiaFile && (
                <p className="text-xs text-black/30 truncate">{midiaFile.name}</p>
              )}
            </div>
          )}

          {/* Mensagem */}
          <div className="space-y-1.5">
            <div className="flex items-center justify-between">
              <label className="text-xs font-medium text-black/60">
                {tipoMensagem === "texto" ? "Mensagem" : "Legenda"}
              </label>
              <button type="button" onClick={inserirVariavel}
                className="text-xs text-black/40 hover:text-black transition flex items-center gap-1">
                + inserir <code className="bg-black/5 px-1 rounded">{"{nome}"}</code>
              </button>
            </div>
            <textarea required rows={4} placeholder={`Olá {nome}, sua presença está confirmada!`}
              value={mensagem} onChange={(e) => setMensagem(e.target.value)}
              className={`${inputCls} resize-none`} />
            <p className="text-xs text-black/30 flex items-center gap-1">
              <Clock className="w-3 h-3" />
              Delay aleatório de 5 a 15 segundos entre cada envio.
            </p>
          </div>

          {erro && (
            <p className="text-sm text-red-500 bg-red-50 border border-red-100 px-4 py-3 rounded-xl">{erro}</p>
          )}
        </form>

        {/* Footer */}
        <div className="flex items-center justify-end gap-2 px-6 py-4 border-t border-black/5">
          {!salvando && (
            <button type="button" onClick={onClose}
              className="px-4 py-2.5 rounded-xl text-sm font-medium text-black/50 hover:text-black hover:bg-black/5 transition">
              Cancelar
            </button>
          )}
          <button type="submit" form="form-campanha" disabled={salvando}
            className="px-5 py-2.5 bg-black text-white text-sm font-medium rounded-xl hover:bg-black/80 transition disabled:opacity-60 flex items-center gap-2 min-w-36 justify-center">
            {salvando ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                <span className="truncate">{etapa || "Criando..."}</span>
              </>
            ) : "Criar campanha"}
          </button>
        </div>
      </div>
    </div>
  );
}
